import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function TrialBalanceReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState("trialBalance");
  
  // Date range for reporting period
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Accounting/ledger-accounts");
      setLedgerAccounts(res.data);
    } catch (err) {
      setError("Failed to load ledger accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute Trial Balance
  const trialBalance = useMemo(() => {
    const accounts = ledgerAccounts.filter(a => a.isActive !== false);
    
    const assets = accounts.filter(a => a.accountType === "Asset");
    const liabilities = accounts.filter(a => a.accountType === "Liability");
    const equity = accounts.filter(a => a.accountType === "Equity");
    const revenue = accounts.filter(a => a.accountType === "Revenue");
    const expenses = accounts.filter(a => a.accountType === "Expense");

    const totalAssets = assets.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalLiabilities = liabilities.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalEquity = equity.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalRevenue = revenue.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalExpenses = expenses.reduce((sum, a) => sum + (a.balance || 0), 0);

    const totalDebits = totalAssets + totalExpenses;
    const totalCredits = totalLiabilities + totalEquity + totalRevenue;

    return {
      assets, liabilities, equity, revenue, expenses,
      totalAssets, totalLiabilities, totalEquity, totalRevenue, totalExpenses,
      totalDebits, totalCredits,
      isBalanced: Math.abs(totalDebits - totalCredits) < 0.01,
      difference: totalDebits - totalCredits
    };
  }, [ledgerAccounts]);

  // Compute Profit & Loss
  const profitLoss = useMemo(() => {
    const revenue = ledgerAccounts.filter(a => a.accountType === "Revenue" && a.isActive !== false);
    const expenses = ledgerAccounts.filter(a => a.accountType === "Expense" && a.isActive !== false);

    const totalRevenue = revenue.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalExpenses = expenses.reduce((sum, a) => sum + (a.balance || 0), 0);
    const netIncome = totalRevenue - totalExpenses;

    return { revenue, expenses, totalRevenue, totalExpenses, netIncome, isProfit: netIncome >= 0 };
  }, [ledgerAccounts]);

  // Compute Balance Sheet
  const balanceSheet = useMemo(() => {
    const assets = ledgerAccounts.filter(a => a.accountType === "Asset" && a.isActive !== false);
    const liabilities = ledgerAccounts.filter(a => a.accountType === "Liability" && a.isActive !== false);
    const equity = ledgerAccounts.filter(a => a.accountType === "Equity" && a.isActive !== false);

    const totalAssets = assets.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalLiabilities = liabilities.reduce((sum, a) => sum + (a.balance || 0), 0);
    const totalEquity = equity.reduce((sum, a) => sum + (a.balance || 0), 0);
    const retainedEarnings = profitLoss.netIncome;
    const totalEquityWithRetained = totalEquity + retainedEarnings;

    return {
      assets, liabilities, equity,
      totalAssets, totalLiabilities, totalEquity,
      retainedEarnings, totalEquityWithRetained,
      totalLiabilitiesAndEquity: totalLiabilities + totalEquityWithRetained,
      isBalanced: Math.abs(totalAssets - (totalLiabilities + totalEquityWithRetained)) < 0.01
    };
  }, [ledgerAccounts, profitLoss]);

  const formatCurrency = (amount) => `₦${Math.abs(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const formatDate = (date) => new Date(date).toLocaleDateString("en-NG", { year: "numeric", month: "long", day: "numeric" });

  const exportToCSV = (reportType) => {
    let csvContent = "\uFEFF";
    const today = new Date().toISOString().split('T')[0];

    if (reportType === "trialBalance") {
      csvContent += "GENERAL LEDGER TRIAL BALANCE\n";
      csvContent += `As at ${formatDate(new Date())}\n\n`;
      csvContent += "Account Code,Account Name,Account Type,Debit,Credit\n";
      
      ledgerAccounts.filter(a => a.isActive !== false).forEach(account => {
        const isDebit = account.accountType === "Asset" || account.accountType === "Expense";
        csvContent += `${account.accountCode || account.accountNumber},"${account.accountName}",${account.accountType},${isDebit ? account.balance : 0},${!isDebit ? account.balance : 0}\n`;
      });
      
      csvContent += `\n,TOTALS,,${trialBalance.totalDebits},${trialBalance.totalCredits}\n`;
      csvContent += `\n,STATUS,,${trialBalance.isBalanced ? "BALANCED" : "UNBALANCED"},\n`;
    } else if (reportType === "profitLoss") {
      csvContent += "PROFIT AND LOSS STATEMENT\n";
      csvContent += `Period: ${formatDate(dateRange.startDate)} to ${formatDate(dateRange.endDate)}\n\n`;
      
      csvContent += "REVENUE\n";
      csvContent += "Account Code,Account Name,Amount\n";
      profitLoss.revenue.forEach(a => csvContent += `${a.accountCode || a.accountNumber},"${a.accountName}",${a.balance}\n`);
      csvContent += `,Total Revenue,${profitLoss.totalRevenue}\n\n`;
      
      csvContent += "EXPENSES\n";
      profitLoss.expenses.forEach(a => csvContent += `${a.accountCode || a.accountNumber},"${a.accountName}",${a.balance}\n`);
      csvContent += `,Total Expenses,${profitLoss.totalExpenses}\n\n`;
      
      csvContent += `,NET ${profitLoss.isProfit ? "PROFIT" : "LOSS"},${profitLoss.netIncome}\n`;
    } else if (reportType === "balanceSheet") {
      csvContent += "BALANCE SHEET\n";
      csvContent += `As at ${formatDate(new Date())}\n\n`;
      
      csvContent += "ASSETS\n";
      balanceSheet.assets.forEach(a => csvContent += `${a.accountCode || a.accountNumber},"${a.accountName}",${a.balance}\n`);
      csvContent += `,Total Assets,${balanceSheet.totalAssets}\n\n`;
      
      csvContent += "LIABILITIES\n";
      balanceSheet.liabilities.forEach(a => csvContent += `${a.accountCode || a.accountNumber},"${a.accountName}",${a.balance}\n`);
      csvContent += `,Total Liabilities,${balanceSheet.totalLiabilities}\n\n`;
      
      csvContent += "EQUITY\n";
      balanceSheet.equity.forEach(a => csvContent += `${a.accountCode || a.accountNumber},"${a.accountName}",${a.balance}\n`);
      csvContent += `,Retained Earnings (Net Income),${balanceSheet.retainedEarnings}\n`;
      csvContent += `,Total Equity,${balanceSheet.totalEquityWithRetained}\n\n`;
      csvContent += `,Total Liabilities & Equity,${balanceSheet.totalLiabilitiesAndEquity}\n`;
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${reportType}_${today}.csv`;
    link.click();
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 General Ledger Reports</h1>
          <p style={styles.subtitle}>Trial Balance, Profit & Loss, Balance Sheet</p>
        </div>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate("/accounting")} style={styles.btnOutline}>← Accounting</button>
          <button onClick={() => navigate("/admin")} style={styles.btnOutline}>← Admin</button>
        </div>
      </div>

      {error && <div style={styles.error}>{error} <button onClick={() => setError("")}>✕</button></div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: "trialBalance", label: "📋 Trial Balance" },
          { id: "profitLoss", label: "💰 Profit & Loss" },
          { id: "balanceSheet", label: "📑 Balance Sheet" }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Action Bar */}
      <div style={styles.actionBar}>
        <div style={styles.dateFilters}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>From:</label>
            <input type="date" value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              style={styles.dateInput} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>To:</label>
            <input type="date" value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              style={styles.dateInput} />
          </div>
        </div>
        <div style={styles.exportButtons}>
          <button onClick={() => exportToCSV(activeTab)} style={styles.btnExport}>📥 Export CSV</button>
          <button onClick={() => window.print()} style={styles.btnPrint}>🖨️ Print</button>
          <button onClick={loadData} style={styles.btnRefresh}>🔄 Refresh</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading financial data...</div>
      ) : (
        <div style={styles.reportContainer}>
          {/* Trial Balance */}
          {activeTab === "trialBalance" && (
            <div style={styles.report}>
              <div style={styles.reportHeader}>
                <h2 style={styles.reportTitle}>GENERAL LEDGER TRIAL BALANCE</h2>
                <p style={styles.reportDate}>As at {formatDate(new Date())}</p>
              </div>

              <div style={styles.summaryGrid}>
                <div style={{ ...styles.summaryCard, borderLeft: "4px solid #28a745" }}>
                  <div style={styles.summaryLabel}>Total Debits</div>
                  <div style={{ ...styles.summaryValue, color: "#28a745" }}>{formatCurrency(trialBalance.totalDebits)}</div>
                </div>
                <div style={{ ...styles.summaryCard, borderLeft: "4px solid #dc3545" }}>
                  <div style={styles.summaryLabel}>Total Credits</div>
                  <div style={{ ...styles.summaryValue, color: "#dc3545" }}>{formatCurrency(trialBalance.totalCredits)}</div>
                </div>
                <div style={{ ...styles.summaryCard, borderLeft: `4px solid ${trialBalance.isBalanced ? "#28a745" : "#dc3545"}`, backgroundColor: trialBalance.isBalanced ? "#d4edda" : "#f8d7da" }}>
                  <div style={styles.summaryLabel}>Status</div>
                  <div style={{ ...styles.summaryValue, color: trialBalance.isBalanced ? "#155724" : "#721c24", fontSize: "16px" }}>
                    {trialBalance.isBalanced ? "✓ BALANCED" : `✗ DIFF: ${formatCurrency(trialBalance.difference)}`}
                  </div>
                </div>
              </div>

              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Code</th>
                      <th style={styles.th}>Account Name</th>
                      <th style={styles.th}>Type</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Debit (₦)</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Credit (₦)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Assets */}
                    {trialBalance.assets.length > 0 && (
                      <>
                        <tr><td colSpan="5" style={styles.sectionHeader}>ASSETS</td></tr>
                        {trialBalance.assets.map((a, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{a.accountCode || a.accountNumber}</td>
                            <td style={styles.td}>{a.accountName}</td>
                            <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: "#28a745" }}>Asset</span></td>
                            <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>{formatCurrency(a.balance)}</td>
                            <td style={{ ...styles.td, textAlign: "right", color: "#999" }}>-</td>
                          </tr>
                        ))}
                        <tr style={styles.subtotalRow}>
                          <td colSpan="3" style={styles.subtotalLabel}>Total Assets</td>
                          <td style={styles.subtotalValue}>{formatCurrency(trialBalance.totalAssets)}</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>-</td>
                        </tr>
                      </>
                    )}

                    {/* Liabilities */}
                    {trialBalance.liabilities.length > 0 && (
                      <>
                        <tr><td colSpan="5" style={styles.sectionHeader}>LIABILITIES</td></tr>
                        {trialBalance.liabilities.map((a, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{a.accountCode || a.accountNumber}</td>
                            <td style={styles.td}>{a.accountName}</td>
                            <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: "#dc3545" }}>Liability</span></td>
                            <td style={{ ...styles.td, textAlign: "right", color: "#999" }}>-</td>
                            <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>{formatCurrency(a.balance)}</td>
                          </tr>
                        ))}
                        <tr style={styles.subtotalRow}>
                          <td colSpan="3" style={styles.subtotalLabel}>Total Liabilities</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>-</td>
                          <td style={styles.subtotalValue}>{formatCurrency(trialBalance.totalLiabilities)}</td>
                        </tr>
                      </>
                    )}

                    {/* Equity */}
                    {trialBalance.equity.length > 0 && (
                      <>
                        <tr><td colSpan="5" style={styles.sectionHeader}>EQUITY</td></tr>
                        {trialBalance.equity.map((a, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{a.accountCode || a.accountNumber}</td>
                            <td style={styles.td}>{a.accountName}</td>
                            <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: "#6c757d" }}>Equity</span></td>
                            <td style={{ ...styles.td, textAlign: "right", color: "#999" }}>-</td>
                            <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>{formatCurrency(a.balance)}</td>
                          </tr>
                        ))}
                        <tr style={styles.subtotalRow}>
                          <td colSpan="3" style={styles.subtotalLabel}>Total Equity</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>-</td>
                          <td style={styles.subtotalValue}>{formatCurrency(trialBalance.totalEquity)}</td>
                        </tr>
                      </>
                    )}

                    {/* Revenue */}
                    {trialBalance.revenue.length > 0 && (
                      <>
                        <tr><td colSpan="5" style={styles.sectionHeader}>REVENUE</td></tr>
                        {trialBalance.revenue.map((a, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{a.accountCode || a.accountNumber}</td>
                            <td style={styles.td}>{a.accountName}</td>
                            <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: "#007bff" }}>Revenue</span></td>
                            <td style={{ ...styles.td, textAlign: "right", color: "#999" }}>-</td>
                            <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>{formatCurrency(a.balance)}</td>
                          </tr>
                        ))}
                        <tr style={styles.subtotalRow}>
                          <td colSpan="3" style={styles.subtotalLabel}>Total Revenue</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>-</td>
                          <td style={styles.subtotalValue}>{formatCurrency(trialBalance.totalRevenue)}</td>
                        </tr>
                      </>
                    )}

                    {/* Expenses */}
                    {trialBalance.expenses.length > 0 && (
                      <>
                        <tr><td colSpan="5" style={styles.sectionHeader}>EXPENSES</td></tr>
                        {trialBalance.expenses.map((a, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{a.accountCode || a.accountNumber}</td>
                            <td style={styles.td}>{a.accountName}</td>
                            <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: "#fd7e14" }}>Expense</span></td>
                            <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>{formatCurrency(a.balance)}</td>
                            <td style={{ ...styles.td, textAlign: "right", color: "#999" }}>-</td>
                          </tr>
                        ))}
                        <tr style={styles.subtotalRow}>
                          <td colSpan="3" style={styles.subtotalLabel}>Total Expenses</td>
                          <td style={styles.subtotalValue}>{formatCurrency(trialBalance.totalExpenses)}</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>-</td>
                        </tr>
                      </>
                    )}

                    {/* Grand Totals */}
                    <tr style={styles.grandTotalRow}>
                      <td colSpan="3" style={styles.grandTotalLabel}>GRAND TOTALS</td>
                      <td style={{ ...styles.grandTotalValue, color: "#28a745" }}>{formatCurrency(trialBalance.totalDebits)}</td>
                      <td style={{ ...styles.grandTotalValue, color: "#dc3545" }}>{formatCurrency(trialBalance.totalCredits)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Profit & Loss */}
          {activeTab === "profitLoss" && (
            <div style={styles.report}>
              <div style={styles.reportHeader}>
                <h2 style={styles.reportTitle}>PROFIT AND LOSS STATEMENT</h2>
                <p style={styles.reportDate}>For the period {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}</p>
              </div>

              <div style={styles.summaryGrid}>
                <div style={{ ...styles.summaryCard, borderLeft: "4px solid #007bff" }}>
                  <div style={styles.summaryLabel}>Total Revenue</div>
                  <div style={{ ...styles.summaryValue, color: "#007bff" }}>{formatCurrency(profitLoss.totalRevenue)}</div>
                </div>
                <div style={{ ...styles.summaryCard, borderLeft: "4px solid #fd7e14" }}>
                  <div style={styles.summaryLabel}>Total Expenses</div>
                  <div style={{ ...styles.summaryValue, color: "#fd7e14" }}>{formatCurrency(profitLoss.totalExpenses)}</div>
                </div>
                <div style={{ ...styles.summaryCard, borderLeft: `4px solid ${profitLoss.isProfit ? "#28a745" : "#dc3545"}`, backgroundColor: profitLoss.isProfit ? "#d4edda" : "#f8d7da" }}>
                  <div style={styles.summaryLabel}>Net {profitLoss.isProfit ? "Profit" : "Loss"}</div>
                  <div style={{ ...styles.summaryValue, color: profitLoss.isProfit ? "#155724" : "#721c24" }}>
                    {profitLoss.isProfit ? "+" : "-"}{formatCurrency(profitLoss.netIncome)}
                  </div>
                </div>
              </div>

              <div style={styles.plContainer}>
                {/* Revenue Section */}
                <div style={styles.plSection}>
                  <div style={styles.plSectionHeader}>
                    <span>📈 REVENUE</span>
                  </div>
                  {profitLoss.revenue.length > 0 ? (
                    profitLoss.revenue.map((a, i) => (
                      <div key={i} style={styles.plItem}>
                        <span><span style={styles.accountCode}>{a.accountCode || a.accountNumber}</span> {a.accountName}</span>
                        <span style={{ color: "#007bff", fontWeight: "600" }}>{formatCurrency(a.balance)}</span>
                      </div>
                    ))
                  ) : (
                    <div style={styles.noData}>No revenue recorded</div>
                  )}
                  <div style={styles.plSectionTotal}>
                    <span>Total Revenue</span>
                    <span>{formatCurrency(profitLoss.totalRevenue)}</span>
                  </div>
                </div>

                {/* Less: Expenses */}
                <div style={styles.plSection}>
                  <div style={styles.plSectionHeader}>
                    <span>📉 LESS: EXPENSES</span>
                  </div>
                  {profitLoss.expenses.length > 0 ? (
                    profitLoss.expenses.map((a, i) => (
                      <div key={i} style={styles.plItem}>
                        <span><span style={styles.accountCode}>{a.accountCode || a.accountNumber}</span> {a.accountName}</span>
                        <span style={{ color: "#fd7e14", fontWeight: "600" }}>({formatCurrency(a.balance)})</span>
                      </div>
                    ))
                  ) : (
                    <div style={styles.noData}>No expenses recorded</div>
                  )}
                  <div style={styles.plSectionTotal}>
                    <span>Total Expenses</span>
                    <span>({formatCurrency(profitLoss.totalExpenses)})</span>
                  </div>
                </div>

                {/* Net Profit/Loss */}
                <div style={{
                  ...styles.netIncomeBox,
                  backgroundColor: profitLoss.isProfit ? "#d4edda" : "#f8d7da",
                  borderColor: profitLoss.isProfit ? "#28a745" : "#dc3545"
                }}>
                  <span style={styles.netIncomeLabel}>
                    {profitLoss.isProfit ? "✓ NET PROFIT" : "✗ NET LOSS"}
                  </span>
                  <span style={{ ...styles.netIncomeAmount, color: profitLoss.isProfit ? "#155724" : "#721c24" }}>
                    {formatCurrency(profitLoss.netIncome)}
                  </span>
                </div>

                {/* Profit Margin */}
                {profitLoss.totalRevenue > 0 && (
                  <div style={styles.marginBox}>
                    <span>Profit Margin</span>
                    <span style={{ fontWeight: "700", color: profitLoss.isProfit ? "#28a745" : "#dc3545" }}>
                      {((profitLoss.netIncome / profitLoss.totalRevenue) * 100).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Balance Sheet */}
          {activeTab === "balanceSheet" && (
            <div style={styles.report}>
              <div style={styles.reportHeader}>
                <h2 style={styles.reportTitle}>BALANCE SHEET</h2>
                <p style={styles.reportDate}>As at {formatDate(new Date())}</p>
              </div>

              <div style={styles.bsContainer}>
                {/* Assets */}
                <div style={styles.bsSection}>
                  <div style={{ ...styles.bsSectionHeader, backgroundColor: "#28a745" }}>ASSETS</div>
                  {balanceSheet.assets.map((a, i) => (
                    <div key={i} style={styles.bsItem}>
                      <span>{a.accountName}</span>
                      <span>{formatCurrency(a.balance)}</span>
                    </div>
                  ))}
                  <div style={styles.bsSectionTotal}>
                    <span>Total Assets</span>
                    <span>{formatCurrency(balanceSheet.totalAssets)}</span>
                  </div>
                </div>

                {/* Liabilities */}
                <div style={styles.bsSection}>
                  <div style={{ ...styles.bsSectionHeader, backgroundColor: "#dc3545" }}>LIABILITIES</div>
                  {balanceSheet.liabilities.map((a, i) => (
                    <div key={i} style={styles.bsItem}>
                      <span>{a.accountName}</span>
                      <span>{formatCurrency(a.balance)}</span>
                    </div>
                  ))}
                  <div style={styles.bsSectionTotal}>
                    <span>Total Liabilities</span>
                    <span>{formatCurrency(balanceSheet.totalLiabilities)}</span>
                  </div>
                </div>

                {/* Equity */}
                <div style={styles.bsSection}>
                  <div style={{ ...styles.bsSectionHeader, backgroundColor: "#6c757d" }}>EQUITY</div>
                  {balanceSheet.equity.map((a, i) => (
                    <div key={i} style={styles.bsItem}>
                      <span>{a.accountName}</span>
                      <span>{formatCurrency(a.balance)}</span>
                    </div>
                  ))}
                  <div style={{ ...styles.bsItem, backgroundColor: profitLoss.isProfit ? "#d4edda" : "#f8d7da" }}>
                    <span>Retained Earnings (Net {profitLoss.isProfit ? "Profit" : "Loss"})</span>
                    <span style={{ color: profitLoss.isProfit ? "#155724" : "#721c24" }}>
                      {profitLoss.isProfit ? "" : "-"}{formatCurrency(balanceSheet.retainedEarnings)}
                    </span>
                  </div>
                  <div style={styles.bsSectionTotal}>
                    <span>Total Equity</span>
                    <span>{formatCurrency(balanceSheet.totalEquityWithRetained)}</span>
                  </div>
                </div>

                {/* Balance Check */}
                <div style={{
                  ...styles.balanceCheck,
                  backgroundColor: balanceSheet.isBalanced ? "#d4edda" : "#f8d7da",
                  borderColor: balanceSheet.isBalanced ? "#28a745" : "#dc3545"
                }}>
                  <div style={styles.balanceCheckRow}>
                    <span>Total Assets</span>
                    <span>{formatCurrency(balanceSheet.totalAssets)}</span>
                  </div>
                  <div style={styles.balanceCheckRow}>
                    <span>Total Liabilities + Equity</span>
                    <span>{formatCurrency(balanceSheet.totalLiabilitiesAndEquity)}</span>
                  </div>
                  <div style={{ ...styles.balanceCheckStatus, color: balanceSheet.isBalanced ? "#155724" : "#721c24" }}>
                    {balanceSheet.isBalanced ? "✓ BALANCED" : "✗ NOT BALANCED"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: "20px", maxWidth: "1200px", margin: "0 auto", fontFamily: "'Segoe UI', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "15px" },
  title: { margin: 0, fontSize: "28px", color: "#1a1a2e" },
  subtitle: { margin: "5px 0 0", color: "#666", fontSize: "14px" },
  headerButtons: { display: "flex", gap: "10px" },
  btnOutline: { padding: "8px 16px", backgroundColor: "white", color: "#007bff", border: "1px solid #007bff", borderRadius: "6px", cursor: "pointer", fontSize: "13px" },
  
  tabs: { display: "flex", gap: "5px", marginBottom: "20px", borderBottom: "2px solid #e9ecef", paddingBottom: "0" },
  tab: { padding: "12px 20px", border: "none", background: "none", cursor: "pointer", fontSize: "14px", fontWeight: "500", color: "#666", borderBottom: "3px solid transparent", marginBottom: "-2px", transition: "all 0.2s" },
  tabActive: { color: "#007bff", borderBottomColor: "#007bff", backgroundColor: "#f8f9fa" },
  
  actionBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px" },
  dateFilters: { display: "flex", gap: "15px", alignItems: "center" },
  filterGroup: { display: "flex", flexDirection: "column", gap: "4px" },
  filterLabel: { fontSize: "11px", color: "#666", fontWeight: "500" },
  dateInput: { padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px" },
  exportButtons: { display: "flex", gap: "10px" },
  btnExport: { padding: "8px 16px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" },
  btnPrint: { padding: "8px 16px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" },
  btnRefresh: { padding: "8px 16px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" },
  
  error: { backgroundColor: "#f8d7da", color: "#721c24", padding: "12px 15px", borderRadius: "6px", marginBottom: "15px", display: "flex", justifyContent: "space-between" },
  loading: { textAlign: "center", padding: "60px", color: "#666", fontSize: "16px" },
  
  reportContainer: { backgroundColor: "white", borderRadius: "10px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" },
  report: { padding: "30px" },
  reportHeader: { textAlign: "center", marginBottom: "30px", paddingBottom: "20px", borderBottom: "2px solid #e9ecef" },
  reportTitle: { margin: "0 0 8px", fontSize: "24px", color: "#1a1a2e", letterSpacing: "1px" },
  reportDate: { margin: 0, color: "#666", fontSize: "14px" },
  
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "30px" },
  summaryCard: { backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "8px", textAlign: "center" },
  summaryLabel: { fontSize: "12px", color: "#666", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" },
  summaryValue: { fontSize: "24px", fontWeight: "700" },
  
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th: { padding: "12px 10px", textAlign: "left", backgroundColor: "#1a1a2e", color: "white", fontWeight: "600", fontSize: "12px", textTransform: "uppercase" },
  tr: { borderBottom: "1px solid #eee" },
  td: { padding: "10px", verticalAlign: "middle" },
  sectionHeader: { padding: "12px 10px", backgroundColor: "#e9ecef", fontWeight: "700", fontSize: "13px", color: "#333" },
  subtotalRow: { backgroundColor: "#f8f9fa" },
  subtotalLabel: { padding: "10px", fontWeight: "600" },
  subtotalValue: { padding: "10px", textAlign: "right", fontWeight: "600" },
  grandTotalRow: { backgroundColor: "#1a1a2e" },
  grandTotalLabel: { padding: "12px 10px", fontWeight: "700", color: "white", fontSize: "14px" },
  grandTotalValue: { padding: "12px 10px", textAlign: "right", fontWeight: "700", fontSize: "14px" },
  badge: { padding: "3px 8px", borderRadius: "12px", fontSize: "10px", color: "white", fontWeight: "500" },
  
  // P&L Styles
  plContainer: { maxWidth: "700px", margin: "0 auto" },
  plSection: { marginBottom: "25px", backgroundColor: "#f8f9fa", borderRadius: "8px", overflow: "hidden" },
  plSectionHeader: { padding: "15px 20px", backgroundColor: "#1a1a2e", color: "white", fontWeight: "600", fontSize: "14px" },
  plItem: { display: "flex", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid #e9ecef" },
  plSectionTotal: { display: "flex", justifyContent: "space-between", padding: "15px 20px", backgroundColor: "#e9ecef", fontWeight: "600" },
  accountCode: { fontFamily: "monospace", fontSize: "11px", color: "#666", marginRight: "10px" },
  noData: { padding: "20px", textAlign: "center", color: "#999", fontStyle: "italic" },
  netIncomeBox: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "25px 30px", borderRadius: "8px", border: "2px solid", marginTop: "20px" },
  netIncomeLabel: { fontSize: "18px", fontWeight: "700" },
  netIncomeAmount: { fontSize: "28px", fontWeight: "700" },
  marginBox: { display: "flex", justifyContent: "space-between", padding: "15px 20px", backgroundColor: "#f8f9fa", borderRadius: "8px", marginTop: "15px", fontSize: "14px" },
  
  // Balance Sheet Styles
  bsContainer: { maxWidth: "700px", margin: "0 auto" },
  bsSection: { marginBottom: "25px", backgroundColor: "#f8f9fa", borderRadius: "8px", overflow: "hidden" },
  bsSectionHeader: { padding: "15px 20px", color: "white", fontWeight: "600", fontSize: "14px" },
  bsItem: { display: "flex", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid #e9ecef" },
  bsSectionTotal: { display: "flex", justifyContent: "space-between", padding: "15px 20px", backgroundColor: "#e9ecef", fontWeight: "700" },
  balanceCheck: { padding: "20px", borderRadius: "8px", border: "2px solid", marginTop: "20px" },
  balanceCheckRow: { display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" },
  balanceCheckStatus: { textAlign: "center", marginTop: "15px", paddingTop: "15px", borderTop: "1px solid", fontSize: "16px", fontWeight: "700" }
};

